import { LIMITS_HUMAN } from '../core/limits';
import type { UiMessages } from './types';

export const uiKo: UiMessages = {
  lang: 'ko',
  brand: 'PDF·인쇄',

  nav: {
    home: '홈',
    tools: {
      merge: 'PDF 합치기',
      extract: '페이지 추출',
      organize: '페이지 정리',
      rotate: '페이지 회전',
    },
    printForms: '인쇄 양식 만들기',
    about: '소개',
    privacy: '개인정보',
    contact: '문의',
    switchLang: 'English',
    switchLangAria: '같은 도구의 영어 페이지로 이동',
    skipToEditor: '편집 화면으로 바로 가기',
  },

  editor: {
    dropTitle: 'PDF 파일을 여기에 끌어다 놓으세요',
    dropHintMulti: '여러 개를 한꺼번에 선택할 수 있습니다. 파일은 이 브라우저 안에서만 열립니다.',
    dropHintSingle: 'PDF 한 개를 선택하세요. 파일은 이 브라우저 안에서만 열립니다.',
    chooseFiles: 'PDF 파일 선택',
    chooseFile: 'PDF 파일 선택',
    addMore: '파일 더 추가',
    replaceFile: '다른 파일로 바꾸기',
    reset: '작업 초기화',
    retry: '다시 시도',
    download: '새 PDF 내려받기',
    working: '처리 중…',
    noScript: '이 도구는 브라우저에서 직접 PDF를 처리하므로 자바스크립트가 필요합니다. 사용 방법과 자주 묻는 질문은 아래에서 그대로 읽을 수 있습니다.',

    filesTitle: '불러온 파일',
    moveFileUp: '이 파일을 앞으로',
    moveFileDown: '이 파일을 뒤로',
    removeFile: '이 파일 제거',

    pagesTitle: '페이지',
    gridAria: '페이지 목록. 화살표 키로 이동하고 스페이스로 선택합니다.',
    emptyGrid: '아직 불러온 페이지가 없습니다.',
    selectAll: '전체 선택',
    clearSelection: '선택 해제',
    deleteSelected: '선택 페이지 삭제',
    rotateLeft: '왼쪽으로 90°',
    rotateRight: '오른쪽으로 90°',
    resetRotation: '회전 되돌리기',
    moveLeft: '앞으로 이동',
    moveRight: '뒤로 이동',
    moveFirst: '맨 앞으로',
    moveLast: '맨 뒤로',
    removePage: '이 페이지 삭제',
    keyboardHint:
      '키보드: 화살표 키로 페이지 이동, 스페이스 또는 Enter로 선택, Ctrl+←/→로 순서 변경, Ctrl+Home/End로 맨 앞·맨 뒤로 보내기.',

    rangeLabel: '추출할 페이지',
    rangePlaceholder: '예: 1,3-5',
    rangeHelp:
      '1부터 시작하는 페이지 번호를 쉼표로 구분합니다. 적은 순서대로 추출되고, 같은 번호를 두 번 적으면 오류로 알려 줍니다.',
    rangeClear: '입력 비우기',
    rangeAll: '전체 페이지',

    thumbnailPending: '미리보기 준비 중',
    thumbnailFailed: '미리보기를 만들지 못했습니다',

    preflightLimits: `파일은 서버로 보내지 않고 이 브라우저에서 처리합니다. 파일당 ${LIMITS_HUMAN.maxFileMiB}MiB · 합계 ${LIMITS_HUMAN.maxTotalMiB}MiB · ${LIMITS_HUMAN.maxTotalPages}페이지 · 파일 ${LIMITS_HUMAN.maxFiles}개까지.`,
    cautionSummary: '전자서명·입력 양식·북마크는 새 파일에 유지되지 않을 수 있습니다.',
    cautionMore: '자세한 제한 보기',
    cautionBody:
      '이 도구는 일반 PDF의 페이지를 다룹니다. 전자서명, 입력 양식(AcroForm) 값, 북마크(목차), 태그 구조는 새로 만든 파일에서 유지되지 않을 수 있습니다. 서명된 문서나 양식 문서는 원본을 따로 보관하세요. 비밀번호가 걸린 PDF는 열지 못하며, 비밀번호 해제 기능은 없습니다. 원본을 브라우저 저장소에 남기지 않으며, 탭을 닫으면 메모리에서 사라집니다.',
    deviceNote: '처리 속도와 실제로 가능한 크기는 기기와 브라우저에 따라 다릅니다.',
  },

  fmt: {
    pageCount: (n) => `${n}페이지`,
    fileCount: (n) => `파일 ${n}개`,
    selectedCount: (n) => (n === 0 ? '선택 없음' : `${n}개 선택됨`),
    pageBadge: (n) => `${n}`,
    pageRef: (n) => `${n}페이지`,
    rotationBadge: (degrees) => `${degrees}°`,
    sizeBadge: (width, height) => `${Math.round(width)}×${Math.round(height)}pt`,
    sourceBadge: (fileName, pageNumber) => `${fileName} · 원본 ${pageNumber}p`,
    orientation: (portrait) => (portrait ? '세로' : '가로'),
    statusLoading: (name) => `${name} 읽는 중…`,
    statusLoaded: (files, pages) => `파일 ${files}개, 총 ${pages}페이지를 불러왔습니다.`,
    statusReset: () => '작업을 초기화했습니다. 불러온 파일과 미리보기를 모두 비웠습니다.',
    statusKept: (files, pages) =>
      `새 파일을 사용하지 않았습니다. 이전 작업(파일 ${files}개, ${pages}페이지)과 선택 내용은 그대로 남아 있습니다.`,
    statusExported: (name, pages) => `${name} (${pages}페이지) 내려받기를 시작했습니다.`,
    statusIgnored: (names) => `이 도구는 파일 한 개만 사용하므로 다음 파일은 제외했습니다: ${names.join(', ')}`,
    statusRangeSelected: (pages) => `${pages}페이지를 추출 대상으로 선택했습니다.`,
    statusDeleted: (n) => `${n}페이지를 삭제했습니다.`,
    statusRotated: (n, degrees) => `${n}페이지를 ${degrees > 0 ? '오른쪽' : '왼쪽'}으로 90° 회전했습니다.`,
    statusMoved: (from, to) => `${from}번째 페이지를 ${to}번째로 옮겼습니다.`,
    fileMeta: (pages, mib) => `${pages}페이지 · ${mib}MiB`,
    exportDisabledNoPages: () => '남은 페이지가 없어 내려받을 수 없습니다. 페이지를 되살리려면 작업을 초기화하세요.',
  },

  errors: {
    notPdf: (p) => `${p.name ?? '선택한 파일'}은(는) PDF 형식이 아닙니다. 확장자가 .pdf인 파일을 선택하세요.`,
    encrypted: (p) =>
      `${p.name ?? '선택한 파일'}은(는) 비밀번호로 보호된 PDF입니다. 이 도구는 비밀번호를 해제하지 않습니다. PDF를 만든 프로그램에서 보호를 해제한 뒤 다시 시도하세요.`,
    corrupt: (p) =>
      `${p.name ?? '선택한 파일'}을(를) 읽지 못했습니다. 파일이 손상되었거나 이 도구가 다루지 못하는 구조입니다.${p.detail ? ` (원인: ${p.detail})` : ''}`,
    noPages: (p) => `${p.name ?? '선택한 파일'}에는 페이지가 없습니다.`,
    emptyFile: (p) => `${p.name ?? '선택한 파일'}의 내용이 비어 있습니다(0바이트).`,
    fileTooLarge: (p) =>
      `${p.name ?? '선택한 파일'}이(가) 파일당 한도를 넘습니다. 한도 ${p.limitMiB}MiB, 선택한 파일 ${p.actualMiB}MiB.`,
    totalTooLarge: (p) =>
      `${p.name ?? '선택한 파일'}을(를) 더하면 합계 한도를 넘습니다. 한도 ${p.limitMiB}MiB, 합계 ${p.actualMiB}MiB. 이미 불러온 파일은 그대로 두었습니다.`,
    tooManyPages: (p) =>
      `${p.name ?? '선택한 파일'}을(를) 더하면 페이지 한도를 넘습니다. 한도 ${p.limitPages}페이지, 합계 ${p.actualPages}페이지. 이미 불러온 파일은 그대로 두었습니다.`,
    tooManyFiles: (p) => `한 번에 다룰 수 있는 파일은 ${p.limitFiles}개까지입니다.`,
    rangeEmpty: (p) => `추출할 페이지를 입력하세요. 예: 1,3-5 (이 문서는 ${p.pageCount}페이지입니다).`,
    rangeSyntax: (p) => `"${p.token}"은(는) 페이지 번호나 범위로 읽을 수 없습니다. 1,3-5 형식으로 입력하세요.`,
    rangeZero: (p) => `페이지 번호는 1부터 시작합니다. "${p.token}"을(를) 고쳐 주세요.`,
    rangeOutOfBounds: (p) =>
      `${p.page}페이지는 이 문서에 없습니다. 이 문서는 ${p.pageCount}페이지입니다("${p.token}" 확인).`,
    rangeReversed: (p) => `"${p.token}"은(는) 시작이 끝보다 큽니다. 작은 번호를 먼저 적어 주세요.`,
    rangeDuplicate: (p) => `${p.page}페이지가 두 번 들어 있습니다("${p.token}"). 같은 페이지는 한 번만 적어 주세요.`,
    noFiles: () => '먼저 PDF 파일을 선택하세요.',
    noPagesLeft: () => '남은 페이지가 없습니다. 내려받을 내용이 없어 내보내기를 멈췄습니다.',
    noSelection: () => '먼저 페이지를 선택하세요.',
    exportFailed: (p) => `새 PDF를 만들지 못했습니다.${p.detail ? ` (원인: ${p.detail})` : ''} 다시 시도해 보세요.`,
    renderFailed: (p) => `미리보기를 만들지 못했습니다.${p.detail ? ` (원인: ${p.detail})` : ''}`,
  },
};
